/**
 * combinedChartOptions.js : séries, couleurs, données et options Chart.js de
 * la Vue combinée (pur). Extrait de CombinedChart.jsx sur le modèle de
 * pmChartOptions.js : le composant garde le rendu et les interactions, la
 * configuration vit ici.
 *
 * Trois axes Y : yLeft (PM1, PM2.5, PM10 en µg/m³, avec les seuils PM en
 * tirets), yRight (température), yRight2 (humidité). Quatrième axe yCo2
 * (CO₂ en ppm, D253 lot 3) seulement quand la session a du CO2 : il n'est
 * dessiné que si la série est visible (display 'auto'), sans ligne de
 * référence (la valeur max recommandée est sur la courbe CO₂ dédiée, qui a
 * son propre repère et sa propre échelle). Sans CO2, données et options sont
 * strictement celles d'avant le lot 3.
 */
import { PM_THRESHOLDS } from '../constants/pmThresholds';
import { getTheme } from '../constants/theme';
import { formatDateLocale, formatNumberLocale } from '../utils/formatters';
import { isFiniteNumber } from '../utils/statsUtils';
import { hasCo2 } from '../sessions/period/periodAnalysis';

// Couleurs des courbes
const COLORS = {
  pm1:         { border: 'rgba(0,206,209,1)',    bg: 'rgba(0,206,209,0.08)'   },
  pm25:        { border: 'rgba(147,112,219,1)',  bg: 'rgba(147,112,219,0.12)' },
  pm10:        { border: 'rgba(255,140,0,1)',    bg: 'rgba(255,140,0,0.1)'    },
  temperature: { border: 'rgba(239,68,68,1)',    bg: 'rgba(239,68,68,0.08)'   },
  humidity:    { border: 'rgba(59,130,246,1)',   bg: 'rgba(59,130,246,0.08)'  },
};

const COLORS_DARK = {
  pm1:         { border: 'rgba(64,224,208,0.9)',  bg: 'rgba(64,224,208,0.08)'  },
  pm25:        { border: 'rgba(186,135,255,0.9)', bg: 'rgba(186,135,255,0.1)'  },
  pm10:        { border: 'rgba(255,183,77,0.9)',  bg: 'rgba(255,183,77,0.08)'  },
  temperature: { border: 'rgba(248,113,113,0.9)', bg: 'rgba(248,113,113,0.07)' },
  humidity:    { border: 'rgba(96,165,250,0.9)',  bg: 'rgba(96,165,250,0.07)'  },
};

const SERIES = [
  { key: 'pm1',         label: 'PM1',                            axis: 'yLeft',   unit: 'µg/m³' },
  { key: 'pm25',        label: 'PM2.5',                          axis: 'yLeft',   unit: 'µg/m³' },
  { key: 'pm10',        label: 'PM10',                           axis: 'yLeft',   unit: 'µg/m³' },
  { key: 'temperature', labelKey: 'charts.series.temperature',   axis: 'yRight',  unit: '°C'   },
  { key: 'humidity',    labelKey: 'charts.series.humidity',      axis: 'yRight2', unit: '%'    },
];

// CO2 (ppm) sur son propre axe, en entiers ; présent seulement si la session en a.
const CO2_SERIES = {
  key: 'co2', labelKey: 'charts.series.co2', axis: 'yCo2', unit: 'ppm', format: (v) => formatNumberLocale(v, 0),
};
// Marge de l'axe CO2 au-dessus du plus haut point et sous le plus bas.
const CO2_AXIS_MARGIN = 0.1;

// Seuils à afficher sur l'axe PM (lignes pointillées) — libellés localisés par buildCombinedSeries
const PM_THRESHOLD_LINES = [
  { value: PM_THRESHOLDS.pm25.warningMax, pm: 'PM2.5', kind: 'warn',  color: 'rgba(239,159,39,0.6)' },
  { value: PM_THRESHOLDS.pm25.alertMax,   pm: 'PM2.5', kind: 'alert', color: 'rgba(216,90,48,0.6)'  },
  { value: PM_THRESHOLDS.pm10.warningMax, pm: 'PM10',  kind: 'warn',  color: 'rgba(255,183,77,0.5)' },
];

/** Visibilité initiale de chaque série (légende cliquable). */
export const INITIAL_VISIBILITY = Object.freeze({
  pm1: true, pm25: true, pm10: true, temperature: true, humidity: true, co2: true,
});

function getReadingValue(r, key) {
  if (key === 'pm25') return r.pm2_5;
  // CO2 absent d'une lecture : trou dans la courbe (null), jamais 0 (D190).
  if (key === 'co2') return isFiniteNumber(r.co2) ? r.co2 : null;
  return r[key];
}

function formatLabel(r) {
  // Label d'axe X (affichage) : heure « HH:MM » localisée (FR/EN identiques, colon).
  return formatDateLocale(r.timestamp, { hour: '2-digit', minute: '2-digit' });
}

/** Couleurs des séries selon le thème ; CO2 par token du thème (même teinte que Co2Chart). */
export function seriesColors(darkMode) {
  const { info } = getTheme(darkMode);
  return { ...(darkMode ? COLORS_DARK : COLORS), co2: { border: info, bg: info } };
}

/** Couleurs du cadre, des textes et de la grille selon le thème. */
export function chartColors(darkMode) {
  return {
    text:    darkMode ? '#E1E1E1' : '#2C3E50',
    textSec: darkMode ? '#94A3B8' : '#4A5568',
    grid:    darkMode ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)',
    card:    darkMode ? '#282845' : '#FFFFFF',
    border:  darkMode ? 'rgba(255,255,255,0.08)' : '#E0E0E0',
  };
}

/**
 * Séries et lignes de seuil localisées (labels traduits, valeurs/PM invariants).
 * PM10 jamais mesuré (ex. capteur externe, D194) : ni série, ni légende, ni
 * ligne de seuil PM10 — une courbe vide à côté d'un seuil laisserait croire
 * à une mesure sous le seuil. Série CO2 seulement si la session en a
 * (hasCo2, même juge que la carte et la courbe dédiée).
 * @returns {{ series: Array, thresholdLines: Array, withCo2: boolean }}
 */
export function buildCombinedSeries(readings, t) {
  const list = readings || [];
  const hasPm10 = list.some(r => typeof r.pm10 === 'number' && Number.isFinite(r.pm10));
  const withCo2 = hasCo2(null, list);
  const series = [...SERIES, ...(withCo2 ? [CO2_SERIES] : [])]
    .filter(s => hasPm10 || s.key !== 'pm10')
    .map(s => ({ ...s, label: s.labelKey ? t(s.labelKey) : s.label }));
  const thresholdLines = PM_THRESHOLD_LINES.filter(l => hasPm10 || l.pm !== 'PM10').map(l => ({
    ...l,
    label: t(`charts.threshold.${l.kind}`, { pm: l.pm, v: l.value }),
  }));
  return { series, thresholdLines, withCo2 };
}

/**
 * Bornes suggérées de l'axe CO2 (ppm) : marge autour des valeurs mesurées,
 * jamais sous zéro ; boucle simple, sans Math.min/max sur des null.
 */
export function co2CombinedBounds(readings) {
  let min = Infinity;
  let max = -Infinity;
  for (const r of readings) {
    const v = r.co2;
    if (!isFiniteNumber(v)) continue;
    if (v < min) min = v;
    if (v > max) max = v;
  }
  if (!Number.isFinite(min)) return { suggestedMin: 0, suggestedMax: 0 };
  return {
    suggestedMin: Math.max(0, Math.floor(min * (1 - CO2_AXIS_MARGIN))),
    suggestedMax: Math.ceil(max * (1 + CO2_AXIS_MARGIN)),
  };
}

/** Données du graphique : une série par grandeur, puis les lignes de seuil PM. */
export function buildCombinedData({ readings, series, thresholdLines, colors, visible }) {
  const datasets = series.map(({ key, label, axis }) => {
    const c = colors[key];
    const isPM = axis === 'yLeft';
    return {
      label,
      data: readings.map(r => getReadingValue(r, key)),
      borderColor: c.border,
      backgroundColor: c.bg,
      borderWidth: isPM ? 2 : 1.5,
      pointRadius: 0,
      pointHoverRadius: 4,
      fill: false,
      tension: 0.3,
      yAxisID: axis,
      hidden: !visible[key],
    };
  });

  // Lignes seuils PM en datasets (type line, no legend, no tooltip)
  const thresholdDatasets = thresholdLines.map(({ value, label, color }) => ({
    label,
    data: readings.map(() => value),
    borderColor: color,
    borderWidth: 1,
    borderDash: [5, 4],
    pointRadius: 0,
    fill: false,
    yAxisID: 'yLeft',
    hidden: !visible.pm25 && !visible.pm10,
  }));

  return { labels: readings.map(formatLabel), datasets: [...datasets, ...thresholdDatasets] };
}

// Axe CO2 : à droite, affiché seulement quand la série est visible ('auto'),
// échelle sur les valeurs mesurées, pas de quadrillage (comme T et HR).
function co2Axis(readings, color, t) {
  return {
    type: 'linear', position: 'right',
    display: 'auto',
    ...co2CombinedBounds(readings),
    title: { display: true, text: t('charts.axis.co2'), color, font: { size: 11 } },
    ticks: { color, font: { size: 11 } },
    grid:  { drawOnChartArea: false },
  };
}

/**
 * Options Chart.js.
 * @param {{ readings, series, darkMode: boolean, annotations: Object, zoom: Object, t: Function,
 *   withCo2?: boolean }} p
 *   `annotations` : annotations Chart.js déjà construites ; `zoom` : options
 *   du plugin de zoom (useChartZoomWindow) ; `withCo2` : axe CO2 ajouté.
 */
export function buildCombinedOptions({ readings, series, darkMode, annotations, zoom, t, withCo2 = false }) {
  const c = chartColors(darkMode);

  // Calcul des plages Y pour chaque axe
  const pmValues   = readings.flatMap(r => [r.pm1, r.pm2_5, r.pm10]).filter(v => v != null);
  const tempValues = readings.map(r => r.temperature).filter(v => v != null);

  const pmMax   = pmValues.length   ? Math.max(...pmValues)   * 1.2 : 100;
  const tempMin = tempValues.length ? Math.min(...tempValues) - 3   : 0;
  const tempMax = tempValues.length ? Math.max(...tempValues) + 3   : 40;

  return {
    responsive: true,
    maintainAspectRatio: false,
    interaction: { mode: 'index', intersect: false },
    plugins: {
      legend: { display: false }, // légende custom dans le composant
      annotation: { annotations },
      tooltip: {
        backgroundColor: darkMode ? '#1E1E32' : '#FFFFFF',
        titleColor: c.text,
        bodyColor: c.textSec,
        borderColor: c.border,
        borderWidth: 1,
        callbacks: {
          label: (ctx) => {
            const serie = series.find(s => s.label === ctx.dataset.label);
            if (!serie || ctx.parsed.y == null) return null;
            const value = serie.format ? serie.format(ctx.parsed.y) : ctx.parsed.y.toFixed(1);
            return ` ${ctx.dataset.label} : ${value} ${serie.unit}`;
          },
        },
      },
      zoom,
    },
    scales: {
      x: {
        ticks: { color: c.textSec, maxTicksLimit: 12, font: { size: 11 } },
        grid:  { color: c.grid },
      },
      yLeft: {
        type: 'linear', position: 'left',
        min: 0, max: pmMax,
        title: { display: true, text: 'PM (µg/m³)', color: c.textSec, font: { size: 11 } },
        ticks: { color: c.textSec, font: { size: 11 } },
        grid:  { color: c.grid },
      },
      yRight: {
        type: 'linear', position: 'right',
        min: tempMin, max: tempMax,
        title: { display: true, text: t('charts.axis.temperature'), color: COLORS.temperature.border, font: { size: 11 } },
        ticks: { color: COLORS.temperature.border, font: { size: 11 } },
        grid:  { drawOnChartArea: false },
      },
      yRight2: {
        type: 'linear', position: 'right',
        min: 0, max: 100,
        offset: true,
        title: { display: true, text: t('charts.axis.humidity'), color: COLORS.humidity.border, font: { size: 11 } },
        ticks: { color: COLORS.humidity.border, font: { size: 11 } },
        grid:  { drawOnChartArea: false },
      },
      ...(withCo2 ? { yCo2: co2Axis(readings, seriesColors(darkMode).co2.border, t) } : {}),
    },
  };
}
